import type { BundleInfo, PartialContext } from '@ms-cloudpack/api-server';
import type {
  BundleMessage,
  PackageImporterContext,
  PackageJson,
  GeneratedPackageSettings,
  InternalBundlerCapabilitiesOptions,
} from '@ms-cloudpack/common-types';
import {
  ensureGeneratedSettingsForPackage,
  getGeneratedPackageSettings,
  getPackageSettings,
} from '@ms-cloudpack/config';
import {
  addExportsMapEntry,
  ensureResolveMapEntry,
  flattenExportsMap,
  getDependencies,
  getExportsMap,
} from '@ms-cloudpack/package-utilities';
import { bulletedList, cyan } from '@ms-cloudpack/task-reporter';
import type { GeneratedPackageChanges } from './types/GeneratedPackageChanges.js';
import { isBuiltin } from 'module';
import { getConsumedPaths } from '@ms-cloudpack/api-server';
import { neverExclude } from './neverExclude.js';
import path from 'path';

export interface EvaluateImportsForOverridesOptions {
  /** Path being evaluated */
  packagePath: string;
  /** Bundle result info for `packagePath` */
  info: BundleInfo;
  /** Package definition for `packagePath` */
  definition: PackageJson;
  /** Info about how the parent package imported `packagePath` (name and version used) */
  importerContext: PackageImporterContext | undefined;
}

export type EvaluateImportsForOverridesContext = PartialContext<'packages', 'config' | 'resolveMap'>;

export interface EvaluateImportsForOverridesResult {
  /** Export changes made to the generated config */
  exportsChanges: GeneratedPackageChanges[];
  /** Included dependencies changes made to the generated config */
  includedDependenciesChanges: GeneratedPackageChanges[];
  /** Required removed excluded dependencies changes made to the generated config */
  removedExcludedDependenciesChanges: GeneratedPackageChanges[];
  /** Any errors encountered */
  errors: BundleMessage[];
  /** Any warnings generated */
  warnings: BundleMessage[];
  /** Map from new package path (to be evaluated) to info about the parent. */
  pathsToEvaluate: Map<string, PackageImporterContext | undefined>;
  /** Package paths for which new exports were added, so the package should be re-processed. */
  pathsToRefresh: Set<string>;
}

/**
 * Evaluate the imports depended on by a package to determine if missing exports map paths need to be added.
 * If so, updates package overrides in the config.
 *
 * **Mutates `context.session.config.generated.packageSettings` and potentially `context.session.resolveMap`.**
 */
export async function evaluateImportsForOverrides(
  options: EvaluateImportsForOverridesOptions,
  context: EvaluateImportsForOverridesContext,
): Promise<EvaluateImportsForOverridesResult> {
  const { info, definition, packagePath, importerContext } = options;
  const { packages, session } = context;
  const { config, resolveMap } = session;
  const { appPath } = config;

  const { name: packageName, version: packageVersion } = definition;
  const displayName = `${packageName}@${packageVersion}`;
  if (!packageName || !packageVersion) {
    throw new Error(`Package "${displayName}" is missing a name or version.`);
  }

  const exportsChanges: GeneratedPackageChanges[] = [];
  const includedDependenciesChanges: GeneratedPackageChanges[] = [];
  const removedExcludedDependenciesChanges: GeneratedPackageChanges[] = [];
  const errors: BundleMessage[] = [];
  const warnings: BundleMessage[] = [];
  const pathsToEvaluate = new Map<string, PackageImporterContext | undefined>();
  const pathsToRefresh = new Set<string>();
  const dependencies = getDependencies(definition);
  const allDependencies = getDependencies(definition, { includeAll: true });
  const originalDefinition = await packages.get(packagePath, { disableTransforms: true });
  const allOriginalDependencies = getDependencies(originalDefinition, { includeAll: true });
  const consumedPaths = getConsumedPaths(info);
  const userExcludedDependencies = getPackageSettings({
    name: packageName,
    version: packageVersion,
    userPackageSettings: config.packageSettings,
    generatedPackageSettings: undefined,
  }).userPackageSettings?.excludedDependencies;

  let generatedPackageSettings: GeneratedPackageSettings | undefined;

  // Iterate through imports and find their path from the resolve map.
  for (const [dependencyName, dependencyImportPaths] of Object.entries(consumedPaths)) {
    // If the dependency is excluded in the user settings, entirely ignore it.
    if (userExcludedDependencies?.includes(dependencyName)) {
      continue;
    }

    // Ensure we use the correct context when evaluating this dependency.
    let dependencyImporterContext: PackageImporterContext | undefined;
    if (allDependencies[dependencyName]) {
      dependencyImporterContext = {
        parentName: packageName,
        parentVersion: packageVersion,
        importedName: dependencyName,
        // TODO should this use the original version requirement even for dev deps (which might not
        // be installed at that version)?
        versionRequirement: allDependencies[dependencyName] || '*',
      };
    }

    // Check if we can resolve the dependency.
    // If we included or removed an excluded dependency, we need to ensure it is in the resolve map.
    // TODO: this is somewhat broken https://github.com/microsoft/cloudpack/issues/2390
    const { dependencyPath, updatedResolution } = await ensureResolveMapEntry(
      {
        definition,
        packagePath,
        dependencyName,
        resolveMap,
        appPath,
        moduleResolutionConfig: config.resolve,
        importerContext: dependencyImporterContext,
      },
      { packages },
    );

    // If the dependency is not resolved, add an error.
    if (!dependencyPath) {
      errors.push(getMissingDepMessage({ dependencyName, displayName, issueType: 'not-found', packagePath }));
      // If the dependency is not resolved, we can't continue to evaluate it.
      continue;
    }

    const dependencyDefinition = await packages.get(dependencyPath);

    const bundlerCapabilities = (dependencyDefinition.cloudpack?.bundlerCapabilities ||
      {}) as InternalBundlerCapabilitiesOptions;
    const dependencyResolveWebExtensions =
      // eslint-disable-next-line etc/no-deprecated
      bundlerCapabilities?.['resolve-web-extensions'] ?? !!config.features?.resolveWebExtensions;

    // If the dependency is not listed, add to includedDependencies.
    if (!dependencies[dependencyName] && !neverExclude(dependencyName, config)) {
      // Survive from the case where it is not a dependency in the package.json
      // and it is resolvable, but warn the user.
      // The parent if-condition (!dependencies[dependencyName]) already checks if it was added
      // in either user or generated configuration, so we only need to warn for truly undeclared deps.
      // Only warn when it is not a self import.
      if (!allOriginalDependencies[dependencyName] && dependencyName !== packageName) {
        warnings.push(getMissingDepMessage({ dependencyName, displayName, issueType: 'undeclared', packagePath }));
      }
      // Make sure to add the package to the list of packages to evaluate.
      // This ensures its PackageJson.cloudpack is updated accounting for the new dependency.
      // TODO could we just refresh the package definition here and skip this step?
      // (does it do anything else besides refreshing the package definition?)
      pathsToEvaluate.set(packagePath, importerContext);

      // Add to includedDependencies if appropriate.
      generatedPackageSettings ??= await ensureGeneratedSettingsForPackage({ packagePath }, { config, packages });

      const isExcludedDependency = generatedPackageSettings.excludedDependencies?.includes(dependencyName);

      // Excluded dependencies that don't update resolution will be handled later during optimize dependencies.
      if (updatedResolution && isExcludedDependency && generatedPackageSettings.excludedDependencies) {
        const indexOfDependency = generatedPackageSettings.excludedDependencies.indexOf(dependencyName);
        generatedPackageSettings.excludedDependencies.splice(indexOfDependency, 1);

        // Report that we removed a dependency from excluded as it is now imported by the package.
        removedExcludedDependenciesChanges.push({
          name: packageName,
          version: packageVersion,
          path: packagePath,
          changes: [
            {
              type: 'removed-excluded-dependency-required',
              change: dependencyName,
              reason: `Imported by "${displayName}"`,
            },
          ],
        });
      } else if (!isExcludedDependency && !generatedPackageSettings.includedDependencies?.includes(dependencyName)) {
        // If the dependency is not excluded, we need to add it to includedDependencies.
        generatedPackageSettings.includedDependencies ??= [];
        generatedPackageSettings.includedDependencies.push(dependencyName);

        includedDependenciesChanges.push({
          name: packageName,
          version: packageVersion,
          path: packagePath,
          changes: [
            {
              // If the resolution was updated, this is a required change.
              type: `included-dependency${updatedResolution ? '-required' : '-optional'}`,
              change: dependencyName,
              reason: `Imported by "${displayName}"`,
            },
          ],
        });
      }
    }

    const { version } = dependencyDefinition;

    // Add the dependency to the list of packages to evaluate.
    pathsToEvaluate.set(dependencyPath, dependencyImporterContext);

    // A flattened, bundle-able exports map for this dependency, which is "built up" over the course of bundling.
    const workingDependencyExportsList = flattenExportsMap(
      await getExportsMap({ packagePath: dependencyPath }, { packages, config }),
    );
    // The official list of exports claimed by this dependency -- used to determine which exports are never used, so we can drop them.
    const canonicalDependencyExports = flattenExportsMap(
      await getExportsMap(
        { packagePath: dependencyPath, disableTransforms: true, resolveWebExtensions: dependencyResolveWebExtensions },
        { packages, config },
      ),
    );

    const initiallyNullExports = getGeneratedPackageSettings({
      name: dependencyName,
      version,
      // Use the generated snapshot to get the previous package settings.
      generatedPackageSettings: config.generatedSnapshot?.packageSettings,
    })?.exports;

    const { userPackageSettings: dependencyUserPackageSettings } = getPackageSettings({
      name: dependencyName,
      version,
      userPackageSettings: config.packageSettings,
      includeBuiltIn: true,
      generatedPackageSettings: [],
    });

    // Only consider removing unused exports if the feature is enabled and there aren't user-provided exports.
    const removeUnusedExports = config.features?.removeUnusedExports && !dependencyUserPackageSettings?.exports;

    let dependencyGeneratedPackageSettings: GeneratedPackageSettings | undefined;
    // iterate through all the import paths.
    for (const dependencyImportPath of dependencyImportPaths) {
      // Imports from paths with trailing slashes are not supported in the exports map.
      if (dependencyImportPath.endsWith('/')) {
        errors.push({
          text: `The import path "${dependencyImportPath}" from "${dependencyName}" has a trailing slash, which is not supported.`,
          source: 'evaluate imports',
          location: { file: packagePath },
        });
        continue;
      }

      if (!removeUnusedExports) {
        const dependencyAlreadyBundled = workingDependencyExportsList[dependencyImportPath];
        if (dependencyAlreadyBundled) {
          continue;
        }
      }

      if (!dependencyGeneratedPackageSettings) {
        // Start with the package's exports all set to null (indicating they are never imported)
        // If this changes, we'll update the generatedPackageSetting accordingly later.
        const nullifiedExports = removeUnusedExports
          ? Object.fromEntries(
              Object.keys(canonicalDependencyExports)
                // Don't nullify the package.json export as it can be very spammy in the generated config
                .filter((k) => k !== './package.json')
                .map((k) => [k, null]),
            )
          : {};

        dependencyGeneratedPackageSettings = await ensureGeneratedSettingsForPackage(
          {
            packagePath: dependencyPath,
            defaultExportsMap: Object.keys(nullifiedExports).length > 0 ? nullifiedExports : undefined,
          },
          { config, packages },
        );
      }

      if (removeUnusedExports) {
        const importHasCorrespondingExport = !!canonicalDependencyExports[dependencyImportPath];
        if (importHasCorrespondingExport) {
          // We're about to remove a null export. Before we do, check if it was initially null in
          // the generated config; if so, we need to track the change so that --check exits correctly
          if (dependencyImportPath in (initiallyNullExports || {})) {
            exportsChanges.push({
              name: dependencyName,
              version: dependencyDefinition.version,
              path: dependencyPath,
              changes: [
                {
                  type: 'changed-export',
                  change: dependencyImportPath,
                  reason: `"${dependencyImportPath}" was previously an unused export; is now used.`,
                },
              ],
            });
          }

          // Since dependencyImportPath is used (and presently set to null),
          // and since we now know it has a corresponding export,
          // delete it from generatePackageSettings.exports so it doesn't get dropped from the bundle
          let deleteSucceeded = false;
          if (
            dependencyGeneratedPackageSettings.exports &&
            dependencyImportPath in dependencyGeneratedPackageSettings.exports
          ) {
            deleteSucceeded = true;
            delete dependencyGeneratedPackageSettings.exports[dependencyImportPath];
          }

          if (deleteSucceeded) {
            // If there was actually an export to delete, mark this path for re-evaluation.
            pathsToRefresh.add(dependencyPath);

            // Delete the entire exports key if it now has zero entries
            if (Object.keys(dependencyGeneratedPackageSettings.exports ?? {}).length === 0) {
              delete dependencyGeneratedPackageSettings.exports;
            }
          }
          continue;
        }
      }

      const dependencyAlreadyBundled = workingDependencyExportsList[dependencyImportPath];
      if (dependencyAlreadyBundled) {
        continue;
      }

      dependencyGeneratedPackageSettings.exports ??= {};

      const addedEntry = await addExportsMapEntry(
        {
          exports: dependencyGeneratedPackageSettings.exports,
          packagePath: dependencyPath,
          importPath: dependencyImportPath,
          resolveWebExtensions: dependencyResolveWebExtensions,
        },
        { packages },
      );

      if (addedEntry) {
        exportsChanges.push({
          name: dependencyName,
          version: dependencyDefinition.version,
          path: dependencyPath,
          changes: [
            {
              type: 'added-export',
              change: dependencyImportPath,
              reason: `Imported by "${displayName}"`,
            },
          ],
        });

        // Since we've found a missing export, we need to re-enqueue the dependency for evaluation.
        pathsToRefresh.add(dependencyPath);
      } else {
        const text =
          `An import couldn't be resolved to a physical file. ` +
          `Check that the import refers to a file that's exported by the package or adjust the "${cyan(
            dependencyName,
          )}" package version to one that supports this import path.\n${bulletedList(
            [
              `Import: ${cyan(dependencyImportPath.replace('.', dependencyName))}`,
              `Resolved to: ${dependencyName}@${version} (${dependencyImportPath})`,
            ],
            2,
          )}`;
        errors.push({
          text,
          source: 'evaluate imports',
          location: { file: packagePath },
        });
      }
    }
  }

  return {
    exportsChanges,
    includedDependenciesChanges,
    removedExcludedDependenciesChanges,
    errors,
    warnings,
    pathsToEvaluate,
    pathsToRefresh,
  };
}

function getMissingDepMessage(params: {
  dependencyName: string;
  displayName: string;
  issueType: 'not-found' | 'undeclared';
  packagePath: string;
}): BundleMessage {
  const { dependencyName, displayName, issueType, packagePath } = params;

  let text: string;
  if (isBuiltin(dependencyName)) {
    text =
      `The node built-in "${dependencyName}" is being imported by "${displayName}", ` +
      `but no browser-friendly version was found in the package's dependencies. ` +
      `This can be fixed by forcing "${displayName}" to use "bundler": "rollup" in package settings, ` +
      `or by adding a polyfill browser-friendly dependency.`;
  } else {
    text = `The package "${dependencyName}" is imported by "${displayName}", but ${
      issueType === 'not-found' ? "it couldn't be found" : "it's not included in the package's dependencies"
    }. Please ensure that "${dependencyName}" is listed in the dependencies of "${displayName}"${
      issueType === 'undeclared' ? ` or add it to "includedDependencies" for "${displayName}" in package settings.` : ''
    }.`;
  }

  return {
    text,
    source: 'evaluate imports',
    location: { file: path.join(packagePath, 'package.json') },
  };
}
